import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '@/lib/api';
import ProbetrainingDialog from '@/components/ProbetrainingDialog';
import ProbetrainingVerschieben from '@/components/ProbetrainingVerschieben';
import { ErgebnisWahl, StatusKnoepfe, Tag1Manuell, ERGEBNIS_LABEL } from '@/components/ProbetrainingErgebnis';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CalendarCheck, Clock, Phone, Mail, ChevronRight, CheckCircle2, CalendarClock, CalendarSync, XCircle, Search, Plus, ArrowUpDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const STATUS = {
  'geplant': { label: 'Geplant', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: CalendarClock },
  'besucht': { label: 'Besucht', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle2 },
  'no_show': { label: 'Nicht erschienen', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
};

const WEEKDAYS = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

function trialDateDE(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso + 'T00:00:00');
    return `${WEEKDAYS[d.getDay()]}, ${d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
  } catch { return iso; }
}

/* Ein Termin ist "abzuhaken", sobald sein Tag angebrochen ist — vorher weiss
   niemand, ob jemand erschienen ist. */
/* Heute als JJJJ-MM-TT in Ortszeit (toISOString wäre UTC und kippt nach Mitternacht) */
function heuteISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function schonFaellig(trialDate) {
  if (!trialDate) return false;
  const heute = new Date(); heute.setHours(0, 0, 0, 0);
  return new Date(trialDate + 'T00:00:00') <= heute;
}

export default function ProbetrainingenPage() {
  const navigate = useNavigate();
  const { userPermissions, isAdminPermissions } = useAuth();
  const darfPflegen = isAdminPermissions || !!userPermissions?.['leads.edit'];
  const [data, setData] = useState({ probetrainings: [], total: 0, geplant: 0, besucht: 0, ohne_termin: 0, ueberfaellig: 0 });
  const [terminFuer, setTerminFuer] = useState(null);   // Lead ohne Termin -> Dialog vorbelegt
  /* Termin verschieben (06.10.2026): mit aktiver Buchung im Termin-Kern die echte Umbuchung. Steht der Termin nur
     am Lead, oder ist die Buchung schon abgesagt („Neu terminiert“ ohne neuen Termin), gibt es nichts umzubuchen:
     dann der Eintragen-Dialog, der eine Buchung anlegt. Kein Knopf, wenn der Termin aus dem Kursplan stammt
     (OneClickGym, „im Kursplan“): Der alte Kursplan-Eintrag bliebe sonst neben dem neuen Termin stehen. */
  const umbuchbar = (pt) => !!pt.probetraining?.buchung_id && pt.probetraining?.status !== 'abgesagt';
  const verschiebbar = (pt) => pt.termin_status === 'geplant' && !!pt.trial_date && (umbuchbar(pt) || !pt.auto_booked);
  const [verschieben, setVerschieben] = useState(null);  // buchung_id
  const [neuerTermin, setNeuerTermin] = useState(false); // Titel des Eintragen-Dialogs
  // Standard: nur was noch kommt (Betreiber 11.10.2026) — „Alle“ steht als letzter Punkt
  const [filter, setFilter] = useState('kommend');
  const [archivArt, setArchivArt] = useState('');      // Archiv: '' | abschluss | verloren
  const [kommendAnzahl, setKommendAnzahl] = useState(null);
  const [query, setQuery] = useState('');
  // Sortierung nach Datum (30.09.2026) — Wahl bleibt je Gerät gemerkt
  const [sortierung, setSortierung] = useState(() => { try { return localStorage.getItem('pt-sortierung') || 'auf'; } catch { return 'auf'; } });
  const sortieren = (v) => { setSortierung(v); try { localStorage.setItem('pt-sortierung', v); } catch { /* ohne Speicher */ } };
  const [loading, setLoading] = useState(true);

  const [eintragen, setEintragen] = useState(false);
  const kommend = (pt) => !pt.abschluss && pt.termin_status === 'geplant' && !!pt.trial_date && pt.trial_date >= heuteISO();
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsAPI.probetrainings({});   // immer alles: gefiltert wird hier, damit die Zahlen an den Knöpfen stehen bleiben
      setData(res.data);
      setKommendAnzahl(res.data.kommend ?? null);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* Archiv (Wunsch Cagla, 24.10.2026): vergangene Probetrainings mit Abschluss oder verloren stehen nur im Reiter „Archiv“ —
     die anderen Reiter (auch „Alle“) zeigen, was noch Arbeit ist. Verloren = Stufe verloren oder Ergebnis „kein Interesse“. */
  const heute = heuteISO();
  const vorbei = (pt) => !pt.trial_date || pt.trial_date < heute;
  const mitAbschluss = (pt) => !!pt.abschluss || pt.probetraining?.ergebnis === 'abschluss';
  const verloren = (pt) => pt.stage === 'verloren' || pt.probetraining?.ergebnis === 'kein_interesse';
  const imArchiv = (pt) => vorbei(pt) && (mitAbschluss(pt) || verloren(pt));
  const alle = data.probetrainings || [];
  const aktiv = alle.filter(pt => !imArchiv(pt));
  const archiv = alle.filter(imArchiv);
  const zahl = {
    kommend: aktiv.filter(kommend).length,
    geplant: aktiv.filter(pt => pt.termin_status === 'geplant' && pt.trial_date && !pt.abschluss).length,
    besucht: aktiv.filter(pt => pt.termin_status === 'besucht').length,
    archiv: archiv.length, archivAbschluss: archiv.filter(mitAbschluss).length, archivVerloren: archiv.filter(pt => !mitAbschluss(pt)).length,
  };
  const q = query.trim().toLowerCase();
  const ohneTermin = (pt) => !pt.abschluss && !pt.trial_date && !['besucht', 'no_show'].includes(pt.termin_status);
  const ueberfaellig = (pt) => !pt.abschluss && pt.termin_status === 'geplant' && schonFaellig(pt.trial_date) && pt.trial_date !== new Date().toISOString().slice(0, 10);
  zahl.ohneTermin = aktiv.filter(ohneTermin).length;
  zahl.ueberfaellig = aktiv.filter(ueberfaellig).length;
  const rows = (filter === 'archiv' ? archiv : aktiv).filter(pt => {
    if (filter === 'archiv' && archivArt === 'abschluss' && !mitAbschluss(pt)) return false;
    if (filter === 'archiv' && archivArt === 'verloren' && mitAbschluss(pt)) return false;
    if (filter === 'kommend' && !kommend(pt)) return false;
    if (filter === 'ohne_termin' && !ohneTermin(pt)) return false;
    if (filter === 'ueberfaellig' && !ueberfaellig(pt)) return false;
    if (filter === 'geplant' && (pt.termin_status !== 'geplant' || !pt.trial_date || pt.abschluss)) return false;
    if (filter === 'besucht' && pt.termin_status !== 'besucht') return false;
    if (!q) return true;
    return [pt.name, pt.email, pt.phone, pt.trial_course, pt.vertrag_tarif].some(v => (v || '').toLowerCase().includes(q));
  }).sort((a, b) => {
    // Ohne Datum immer ans Ende; sonst nach Datum + Uhrzeit
    const ka = a.trial_date ? `${a.trial_date} ${a.trial_time || '00:00'}` : null;
    const kb = b.trial_date ? `${b.trial_date} ${b.trial_time || '00:00'}` : null;
    if (!ka || !kb) return ka ? -1 : kb ? 1 : 0;
    return sortierung === 'ab' ? kb.localeCompare(ka) : ka.localeCompare(kb);
  });
  // Lead ohne Buchung im Termin-Kern: Status direkt am Lead setzen (Da / Nicht da)
  const vorbelegt = (pt) => ({ vorname: (pt.name || '').split(' ').slice(0, -1).join(' ') || (pt.name || ''), nachname: (pt.name || '').split(' ').length > 1 ? (pt.name || '').split(' ').slice(-1)[0] : '', telefon: pt.phone || '', email: pt.email || '',
    // Name stammt vom Lead (oft der Elternteil): bei „Kind“ erscheint damit das eigene Feld für den Kindernamen
    kind_vorname: '' });

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
          <CalendarCheck className="w-6 h-6 text-cyan-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Probetrainings</h1>
          <p className="text-sm text-muted-foreground">Alle gebuchten Probetrainings im Überblick</p>
        </div>
        <Button size="sm" className="ml-auto gap-1.5" onClick={() => setEintragen(true)} data-testid="pt-eintragen"><Plus className="w-4 h-4" /> Probetraining eintragen</Button>
      </div>
      <ProbetrainingDialog offen={eintragen || !!terminFuer} onClose={() => { setEintragen(false); setTerminFuer(null); setNeuerTermin(false); }} onFertig={fetchData} vorbelegt={terminFuer ? vorbelegt(terminFuer) : undefined}
        titel={terminFuer && neuerTermin ? 'Neuen Termin eintragen' : undefined} />
      <ProbetrainingVerschieben buchungId={verschieben} onClose={() => setVerschieben(null)} onFertig={fetchData} />

      {/* Filter-Chips */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: 'kommend', label: `Kommende${kommendAnzahl == null ? '' : ` (${zahl.kommend})`}` },
          { key: 'geplant', label: `Geplant (${zahl.geplant})` },
          { key: 'ohne_termin', label: `Ohne Termin (${zahl.ohneTermin})` },
          { key: 'ueberfaellig', label: `Überfällig (${zahl.ueberfaellig})` },
          { key: 'besucht', label: `Besucht (${zahl.besucht})` },
          { key: '', label: `Alle offenen (${aktiv.length})` },
          { key: 'archiv', label: `Archiv (${zahl.archiv})` },
        ].map(c => (
          <button
            key={c.key || 'all'}
            onClick={() => setFilter(c.key)}
            data-testid={`pt-filter-${c.key || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === c.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-muted/40'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>
      {filter === 'archiv' && (
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="pt-archiv-art">
          <span className="text-muted-foreground">Vergangene Probetrainings mit Abschluss oder verloren:</span>
          {[['', `Alle (${zahl.archiv})`], ['abschluss', `Abschluss (${zahl.archivAbschluss})`], ['verloren', `Verloren (${zahl.archivVerloren})`]].map(([k, l]) => (
            <button key={k || 'alle'} onClick={() => setArchivArt(k)}
              className={`px-2.5 py-1 rounded-full border ${archivArt === k ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:bg-muted/40'}`}>{l}</button>
          ))}
        </div>
      )}

      {/* Suche + Sortierung */}
      <div className="flex flex-wrap items-center gap-2">
      <div className="relative max-w-md flex-1 min-w-[220px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Nach Name, E-Mail, Telefon oder Kurs suchen…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9 h-9"
          data-testid="pt-search-input"
        />
      </div>
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <ArrowUpDown className="w-3.5 h-3.5" />
        <select value={sortierung} onChange={(e) => sortieren(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground" data-testid="pt-sortierung">
          <option value="auf">Datum: älteste zuerst</option>
          <option value="ab">Datum: neueste zuerst</option>
        </select>
      </label>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-16 text-muted-foreground text-sm">Wird geladen…</div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <CalendarCheck className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{filter === 'kommend' ? 'Aktuell stehen keine Probetrainings an' : 'Noch keine Probetrainings gebucht'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map(pt => {
            const st = STATUS[pt.termin_status] || STATUS['geplant'];
            const StIcon = st.icon;
            const dateStr = trialDateDE(pt.trial_date);
            return (
              /* Bewusst ein div statt eines Buttons: Ein Button in einem
                 Button ist ungueltiges Markup, und die Aktionsknoepfe rechts
                 wuerden dann nicht mehr zuverlaessig klicken. */
              <div
                key={pt.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/leads/${pt.id}`)}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) navigate(`/leads/${pt.id}`); }}
                data-testid={`pt-row-${pt.id}`}
                className="w-full flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-border/60 bg-card hover:bg-muted/40 transition-colors px-4 py-3 text-left cursor-pointer"
              >
                {/* Datum-Block */}
                <div className="w-24 flex-shrink-0">
                  {dateStr ? (
                    <>
                      <p className="text-sm font-semibold leading-tight">{dateStr.split(',')[0]}</p>
                      <p className="text-xs text-muted-foreground">{dateStr.split(', ')[1]}</p>
                      {pt.trial_time && (
                        <p className="text-xs text-cyan-600 font-medium flex items-center gap-1 mt-0.5">
                          <Clock className="w-3 h-3" />{pt.trial_time}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-xs text-amber-600 font-medium">Termin fehlt</p>
                  )}
                </div>

                {/* Name + Kurs + Kontakt — immer mit Mindestbreite; reicht der Platz neben Datum, Status und Knöpfen nicht, bricht die Zeile um
                    (31.10.2026: mit den drei Ergebnis-Knöpfen stand sonst nur „Te…“ da) */}
                <div className="flex-1 min-w-[9rem]">
                  <p className="text-sm font-semibold truncate">{pt.name}</p>
                  {pt.trial_course && <p className="text-xs text-muted-foreground truncate">{pt.trial_course}</p>}
                  {/* Abschluss, dessen Probetraining nur am Vertrag steht (13.10.2026) */}
                  {pt.aus_vertrag && <p className="text-xs text-muted-foreground truncate" data-testid={`pt-aus-vertrag-${pt.id}`}>Probetraining laut Vertrag{pt.vertrag_tarif ? ` · ${pt.vertrag_tarif}` : ''}</p>}
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
                    {pt.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{pt.phone}</span>}
                    {pt.email && <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{pt.email}</span>}
                  </div>
                </div>

                {/* Badges */}
                <div className="flex flex-col items-start lg:items-end gap-1 flex-shrink-0">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${ueberfaellig(pt) ? 'bg-red-100 text-red-700' : st.cls}`}>
                    <StIcon className="w-3 h-3" />{ueberfaellig(pt) ? 'nicht bestätigt' : ohneTermin(pt) ? 'ohne Termin' : st.label}
                  </span>
                  {pt.auto_booked && (
                    <span className="text-[10px] text-emerald-600 font-medium">✓ im Kursplan</span>
                  )}
                  {pt.termin_quelle === 'naechster_schritt' && (
                    <span className="text-[10px] text-muted-foreground" title="Termin stammt aus dem nächsten Schritt am Lead, nicht aus einer Kursbuchung">aus nächstem Schritt</span>
                  )}
                  {pt.source && <span className="text-[10px] text-muted-foreground">{pt.source}</span>}
                  {pt.gebucht_von && <span className="text-[10px] text-muted-foreground" data-testid={`pt-von-${pt.id}`}>{pt.aus_vertrag ? 'abgeschlossen von' : 'eingetragen von'} {pt.gebucht_von}</span>}
                </div>
                {/* Abhaken — nur wenn eine echte Buchung dahintersteht und
                    der Termin schon war. */}
                {darfPflegen && ohneTermin(pt) && (
                  <span className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => setTerminFuer(pt)} data-testid={`pt-termin-${pt.id}`}>
                      <CalendarClock className="w-3.5 h-3.5" /> Termin eintragen
                    </Button>
                  </span>
                )}
                {/* Da / Nicht da: ein Baustein für Liste, Lead-Ansicht und Closer (30.10.2026) */}
                {darfPflegen && pt.termin_status === 'geplant' && schonFaellig(pt.trial_date) && (
                  <StatusKnoepfe pt={pt} onFertig={fetchData} />
                )}
                {/* Verschieben: solange der Termin geplant ist — auch wenn er schon vorbei ist (nicht gekommen, meldet sich) */}
                {darfPflegen && verschiebbar(pt) && (
                  <span className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-muted-foreground" title="Termin verschieben"
                            onClick={() => { if (umbuchbar(pt)) setVerschieben(pt.probetraining.buchung_id); else { setNeuerTermin(true); setTerminFuer(pt); } }}
                            data-testid={`pt-verschieben-${pt.id}`}>
                      <CalendarSync className="w-3.5 h-3.5" /> Verschieben
                    </Button>
                  </span>
                )}
                {darfPflegen && (pt.probetraining?.buchung_id || pt.probetraining?.am_lead) && pt.probetraining?.status === 'erschienen' && !pt.probetraining?.ergebnis && (
                  <ErgebnisWahl pt={pt} onFertig={fetchData} navigate={navigate} />
                )}
                {/* Tag 1 nachträglich (02.11.2026): vergangene Termine ohne laufendes Tag-1-Angebot, auch nicht erschienen */}
                {darfPflegen && pt.trial_date && pt.trial_date <= heute && !mitAbschluss(pt) && !verloren(pt) && !pt.bestandskunde
                  && !(pt.tag1_angebot?.bis && pt.tag1_angebot.bis >= heute)
                  && !((pt.probetraining?.buchung_id || pt.probetraining?.am_lead) && pt.probetraining?.status === 'erschienen' && !pt.probetraining?.ergebnis)
                  && <Tag1Manuell pt={pt} onFertig={fetchData} />}
                {pt.bestandskunde && (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 bg-sky-100 text-sky-700" title="Ist schon Mitglied und hat ein neues Probetraining gebucht, zum Beispiel für die Kinder">
                    Bereits Mitglied
                  </span>
                )}
                {pt.abschluss && pt.probetraining?.ergebnis !== 'abschluss' && (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 bg-emerald-100 text-emerald-700" data-testid={`pt-abschluss-${pt.id}`}>
                    {pt.abschluss.vertragsnummer ? `Vertrag ${pt.abschluss.vertragsnummer}` : 'Mitglied'}{pt.abschluss.fuer_kind ? ' · fürs Kind' : ''}
                  </span>
                )}
                {pt.tag1_angebot?.bis && !mitAbschluss(pt) && !verloren(pt) && pt.tag1_angebot.bis >= heute && (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                        title="Mit dem Tag-1-Angebot beraten: Betreuter Einstieg 99 statt 149 € gilt bis zu diesem Tag" data-testid={`pt-tag1-${pt.id}`}>
                    Tag 1 bis {pt.tag1_angebot.bis.slice(8, 10)}.{pt.tag1_angebot.bis.slice(5, 7)}.
                  </span>
                )}
                {pt.probetraining?.ergebnis && (
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${pt.probetraining.ergebnis === 'abschluss' ? 'bg-emerald-100 text-emerald-700' : pt.probetraining.ergebnis === 'bedenkzeit' ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}
                        title={pt.probetraining.verlustgrund ? `Grund: ${pt.probetraining.verlustgrund}` : undefined}>
                    {ERGEBNIS_LABEL[pt.probetraining.ergebnis] || pt.probetraining.ergebnis}
                  </span>
                )}
                <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0 hidden lg:block" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
